import React from "react";
import { Link } from "react-router-dom";
import { Logo } from "./Logo";
import { Phone, Mail, MapPin } from "lucide-react";
import { smoothScrollTo } from "@/lib/SmoothScroll";

const REGIONS = [
  "United Arab Emirates",
  "United States",
  "United Kingdom",
  "Germany",
  "Saudi Arabia",
  "Singapore",
  "Malaysia",
  "Vietnam",
  "Bangladesh",
  "Kenya",
  "South Africa",
  "Australia",
];

export const Footer = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) smoothScrollTo(el, { offset: -80 });
  };

  return (
    <footer
      className="relative bg-[#060F1E] text-white overflow-hidden"
      data-testid="site-footer"
    >
      {/* Marquee */}
      <div className="border-y border-white/10 py-6 overflow-hidden">
        <div className="marquee-track whitespace-nowrap">
          {[...REGIONS, ...REGIONS].map((r, i) => (
            <span
              key={`${r}-${i}`}
              className="font-display text-xl md:text-2xl italic text-white/40 flex items-center gap-12"
            >
              {r}
              <span className="text-[#D4AF37] not-italic">◆</span>
            </span>
          ))}
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Logo variant="dark" size="md" />
            <p className="mt-6 text-sm text-white/65 leading-relaxed max-w-md">
              Rahilaxmi Exports LLP is a Pune-based exporter of premium Indian
              agricultural commodities — Haldi, spices, vegetables, onion
              powder and coconut shell powder — serving buyers across 25+
              countries.
            </p>
          </div>

          <div className="md:col-span-3">
            <div className="font-accent text-[10px] text-[#D4AF37] mb-5">
              Navigate
            </div>
            <ul className="space-y-3 text-sm">
              {[
                { label: "Home", id: "home" },
                { label: "About", id: "about" },
                { label: "Products", id: "products" },
                { label: "Process", id: "process" },
                { label: "Get a Quote", id: "inquiry" },
              ].map((l) => (
                <li key={l.id}>
                  <Link
                    to="/"
                    onClick={(e) => {
                      if (window.location.pathname === "/") {
                        e.preventDefault();
                        scrollTo(l.id);
                      } else {
                        // let router handle navigation, then scroll on load via hash
                        window.location.hash = l.id;
                      }
                    }}
                    className="text-white/70 hover:text-[#D4AF37] transition-colors"
                    data-testid={`footer-link-${l.id}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="font-accent text-[10px] text-[#D4AF37] mb-5">
              Contact
            </div>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <Phone size={14} className="text-[#D4AF37] mt-1" />
                <div>
                  <a
                    href="tel:+917276863734"
                    className="block text-white/80 hover:text-white"
                    data-testid="footer-phone-1"
                  >
                    +91 7276863734
                  </a>
                  <a
                    href="tel:+918446226916"
                    className="block text-white/80 hover:text-white"
                    data-testid="footer-phone-2"
                  >
                    +91 8446226916
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={14} className="text-[#D4AF37] mt-1" />
                <a
                  href="mailto:rltrading2011@gmail.com"
                  className="text-white/80 hover:text-white break-all"
                  data-testid="footer-email"
                >
                  rltrading2011@gmail.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={14} className="text-[#D4AF37] mt-1" />
                <span className="text-white/80" data-testid="footer-address">
                  Chinchwad, Pune 411019
                  <br />
                  Maharashtra, India
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Massive wordmark */}
      <div className="border-t border-white/10 overflow-hidden">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 pt-12 pb-4">
          <div
            className="font-display font-bold text-white/[0.06] leading-none tracking-tighter select-none pointer-events-none italic"
            aria-hidden="true"
            style={{ fontSize: "clamp(4rem, 18vw, 22rem)" }}
          >
            Rahilaxmi
          </div>
        </div>
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 pb-8 flex flex-col md:flex-row justify-between gap-4 text-xs text-white/50">
          <div data-testid="footer-copyright">
            © {new Date().getFullYear()} Rahilaxmi Exports LLP. All rights
            reserved.
          </div>
          <div className="font-accent">Crafted with care in Pune, India.</div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
